import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  useSearchCrmClientsQuery,
  useGetCrmSummaryQuery,
  useGetCrmOptionsQuery,
  useAskCrmMutation,
} from '../../app/api';
import { useIsMobile, gemstoneInfo } from '../utils';

const work = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  ink:       '#0a2947',
  muted:     '#6b7280',
  hairline:  '#dde2e8',
  surface:   '#ffffff',
  canvas:    '#f3f5f8',
};

const PAGE_SIZE = 50;
const LETTERS = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

// Product buckets, in display order. Keys match crm_clients.has_* columns.
export const PRODUCTS = [
  { key: 'aum',      label: 'AUM / Investments', short: 'AUM',      icon: 'graph-up-arrow' },
  { key: 'annuity',  label: 'Annuity',           short: 'Annuity',  icon: 'piggy-bank' },
  { key: 'life',     label: 'Life Insurance',    short: 'Life',     icon: 'umbrella' },
  { key: 'medicare', label: 'Medicare',          short: 'Medicare', icon: 'hospital' },
  { key: 'health',   label: 'Health & LTC',      short: 'Health',   icon: 'bandaid' },
  { key: 'tax',      label: 'Tax',               short: 'Tax',      icon: 'receipt' },
  { key: 'planning', label: 'Financial Plan',    short: 'Plan',     icon: 'clipboard-data' },
];

const STATUS_GROUPS = [
  ['clients', 'Clients'],
  ['prospects', 'Prospects'],
  ['former', 'Former'],
  ['all', 'Everyone'],
];

const SOURCE_GEM_COLORS = { pearl: '#d8d3c4', pebble: '#9d9fa1', cold: '#7fb3d5' };

const fmtInt = (n) => Number(n || 0).toLocaleString();
const fmtMoney = (n) => (n ? Number(n).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '-');
const pct = (n, d) => (d ? Math.round((100 * n) / d) : 0);

/** Gemstone as the source CRM spells it, coloured by the hub tier when it maps. */
export function GemBadge({ value, gemstone }) {
  if (!value) return <span style={{ color: work.muted }}>-</span>;
  const hub = gemstoneInfo(gemstone);
  const color = hub ? hub.color : (SOURCE_GEM_COLORS[value.toLowerCase()] || '#c4cbd4');
  return (
    <span
      title={hub ? `${value} (hub tier ${hub.label})` : `${value} - not mapped to a hub tier`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontWeight: 700, fontSize: '0.75rem', color: work.ink, whiteSpace: 'nowrap' }}
    >
      <i className={`bi ${hub ? 'bi-suit-diamond-fill' : 'bi-circle-fill'}`} style={{ color, fontSize: hub ? '0.85rem' : '0.6rem' }} />
      {value}
    </span>
  );
}
GemBadge.propTypes = { value: PropTypes.string, gemstone: PropTypes.string };

export function ProductPills({ row }) {
  const held = PRODUCTS.filter((p) => row[`has_${p.key}`]);
  if (!held.length) return <span style={{ color: work.muted, fontSize: '0.8rem' }}>-</span>;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '0.25rem' }}>
      {held.map((p) => (
        <span
          key={p.key}
          title={p.label}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
            background: 'rgba(0,105,166,0.08)', color: work.tealBlue,
            border: '1px solid rgba(0,105,166,0.2)', borderRadius: 4,
            padding: '0.05rem 0.35rem', fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap',
          }}
        >
          <i className={`bi bi-${p.icon}`} />{p.short}
        </span>
      ))}
    </span>
  );
}
ProductPills.propTypes = { row: PropTypes.object.isRequired };

function StatTile({ label, value, note, hero = false }) {
  return (
    <div style={{
      background: work.surface, border: `1px solid ${work.hairline}`,
      borderTop: hero ? `3px solid ${work.deepBlue}` : `1px solid ${work.hairline}`,
      borderRadius: 6, padding: '0.85rem 1rem', minWidth: 0,
    }}>
      <div style={{ color: work.muted, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ color: work.ink, fontWeight: 700, lineHeight: 1.1, fontSize: hero ? '2rem' : '1.5rem' }}>{value}</div>
      {note && <div style={{ marginTop: 6, fontSize: '0.76rem', color: work.muted }}>{note}</div>}
    </div>
  );
}
StatTile.propTypes = { label: PropTypes.string, value: PropTypes.node, note: PropTypes.node, hero: PropTypes.bool };

/** A product bucket as a clickable count with a share bar - clicking filters the list. */
function ProductTile({ product, count, total, active, onClick }) {
  const share = pct(count, total);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={active ? 'Click to clear this filter' : `Show only ${product.label} clients`}
      style={{
        textAlign: 'left', cursor: 'pointer', background: active ? 'rgba(0,105,166,0.06)' : work.surface,
        border: `1px solid ${active ? work.tealBlue : work.hairline}`, borderRadius: 6,
        padding: '0.7rem 0.85rem', minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: work.muted, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
        <i className={`bi bi-${product.icon}`} style={{ color: work.goldenrod, fontSize: '0.85rem' }} />
        {product.label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', marginTop: 4 }}>
        <span style={{ color: work.ink, fontWeight: 700, fontSize: '1.3rem' }}>{fmtInt(count)}</span>
        <span style={{ color: work.muted, fontSize: '0.75rem' }}>{share}%</span>
      </div>
      <div style={{ height: 4, background: work.hairline, borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
        <div style={{ width: `${share}%`, height: '100%', background: work.tealBlue }} />
      </div>
    </button>
  );
}
ProductTile.propTypes = {
  product: PropTypes.object.isRequired, count: PropTypes.number, total: PropTypes.number,
  active: PropTypes.bool, onClick: PropTypes.func,
};

function SortHeader({ label, field, sort, setSort, className }) {
  const active = sort === field;
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => setSort(field)}
        style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: active ? work.tealBlue : 'inherit', cursor: 'pointer', textTransform: 'inherit', letterSpacing: 'inherit' }}
      >
        {label}{active && <i className="bi bi-caret-down-fill" style={{ marginLeft: 3, fontSize: '0.6rem' }} />}
      </button>
    </th>
  );
}
SortHeader.propTypes = {
  label: PropTypes.string, field: PropTypes.string, sort: PropTypes.string,
  setSort: PropTypes.func, className: PropTypes.string,
};

/**
 * Work -> CRM: every client of the merging firms in one searchable book.
 *
 * Reads crm_clients, which is BUILT from each firm's own CRM (Redtail or
 * SmartOffice exports; Redtail for
 * Hammer first) - see api/app/db/crm_clients.py. Duplicates in the source CRM
 * are grouped, not hidden: the list shows one row per person with a "records"
 * badge, and the detail page lists every record behind it.
 *
 * The tiles take the same filters as the table, so every number on the page
 * describes the rows underneath it. Product tiles double as filters.
 */
export default function Crm() {
  const isMobile = useIsMobile();
  // A hub link (e.g. from @Hub in Slack) opens with its filters applied.
  const [init] = useState(() => readUrlFilters(window.location.search));
  const [queryInput, setQueryInput] = useState(init.q);
  const [query, setQuery] = useState(init.q);
  const [source, setSource] = useState(init.source);
  const [statusGroup, setStatusGroup] = useState(init.status_group);
  const [advisor, setAdvisor] = useState(init.advisor);
  const [gemstone, setGemstone] = useState(init.gemstone);
  const [product, setProduct] = useState('');
  const [taxMethod, setTaxMethod] = useState(init.tax_method);
  const [taxPreparer, setTaxPreparer] = useState('');
  // Set only by Ask - the page has no control of its own for these.
  const [products, setProducts] = useState(init.products);
  const [excludeProducts, setExcludeProducts] = useState(init.exclude_products);
  const [city, setCity] = useState(init.city);
  const [stateCode, setStateCode] = useState(init.state);
  const [ageMin, setAgeMin] = useState(init.age_min);
  const [ageMax, setAgeMax] = useState(init.age_max);
  const [contactType, setContactType] = useState(init.contact_type);
  const [status, setStatus] = useState(init.status);
  const [askOpen, setAskOpen] = useState(false);
  const [letter, setLetter] = useState('');
  const [reviewDups, setReviewDups] = useState(init.possible_duplicates);
  const [withDocs, setWithDocs] = useState(init.with_documents);
  const [sort, setSort] = useState('name');
  const [page, setPage] = useState(0);

  // Debounce typing so the list and tiles refetch once per pause, not per key.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(queryInput.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [queryInput]);

  const set = (setter) => (value) => { setter(value); setPage(0); };
  const askOnly = [
    ...products.map((k) => ({ key: `p-${k}`, label: `Has ${productName(k)}`, clear: () => setProducts(products.filter((x) => x !== k)) })),
    ...excludeProducts.map((k) => ({ key: `x-${k}`, label: `No ${productName(k)}`, clear: () => setExcludeProducts(excludeProducts.filter((x) => x !== k)) })),
    city && { key: 'city', label: `City: ${city}`, clear: () => setCity('') },
    stateCode && { key: 'state', label: `State: ${stateCode}`, clear: () => setStateCode('') },
    ageMin !== null && { key: 'amin', label: `Age ${ageMin}+`, clear: () => setAgeMin(null) },
    ageMax !== null && { key: 'amax', label: `Age up to ${ageMax}`, clear: () => setAgeMax(null) },
    contactType && { key: 'ctype', label: contactType === 'Business' ? 'Businesses' : 'Individuals', clear: () => setContactType('') },
    status && { key: 'status', label: `Status: ${status}`, clear: () => setStatus('') },
  ].filter(Boolean);
  const hasFilters = Boolean(query || source || advisor || gemstone || product || taxMethod || taxPreparer || letter || reviewDups || withDocs || statusGroup !== 'clients' || askOnly.length);
  const clearAll = () => {
    setQueryInput(''); setQuery(''); setSource(''); setStatusGroup('clients'); setAdvisor('');
    setGemstone(''); setProduct(''); setTaxMethod(''); setTaxPreparer(''); setLetter(''); setReviewDups(false); setWithDocs(false);
    setProducts([]); setExcludeProducts([]); setCity(''); setStateCode(''); setAgeMin(null); setAgeMax(null);
    setContactType(''); setStatus(''); setPage(0);
  };

  // What Ask starts from, and how its answer lands: it REPLACES the filters
  // (a follow-up carries the earlier ones forward itself), so the controls
  // always show what the list is doing.
  const askCurrent = {
    q: query || undefined, source: source || undefined, status_group: statusGroup, status: status || undefined,
    advisor: advisor || undefined, gemstone: gemstone || undefined, contact_type: contactType || undefined,
    products: products.length ? products : undefined, exclude_products: excludeProducts.length ? excludeProducts : undefined,
    tax_method: taxMethod || undefined, city: city || undefined, state: stateCode || undefined,
    age_min: ageMin ?? undefined, age_max: ageMax ?? undefined,
    possible_duplicates: reviewDups || undefined, with_documents: withDocs || undefined,
  };
  const applyAsk = (f) => {
    setQueryInput(f.q || ''); setQuery(f.q || ''); setSource(f.source || ''); setStatusGroup(f.status_group || 'clients');
    setStatus(f.status || ''); setAdvisor(f.advisor || ''); setGemstone(f.gemstone || ''); setContactType(f.contact_type || '');
    setProducts(f.products || []); setExcludeProducts(f.exclude_products || []); setTaxMethod(f.tax_method || '');
    setCity(f.city || ''); setStateCode(f.state || ''); setAgeMin(f.age_min ?? null); setAgeMax(f.age_max ?? null);
    setReviewDups(Boolean(f.possible_duplicates)); setWithDocs(Boolean(f.with_documents));
    setProduct(''); setTaxPreparer(''); setLetter(''); setPage(0);
  };

  const filters = {
    q: query || undefined,
    source: source || undefined,
    status_group: statusGroup,
    advisor: advisor || undefined,
    gemstone: gemstone || undefined,
    product: product || undefined,
    tax_method: taxMethod || undefined,
    tax_preparer: taxPreparer || undefined,
    products: products.join(',') || undefined,
    exclude_products: excludeProducts.join(',') || undefined,
    city: city || undefined,
    state: stateCode || undefined,
    age_min: ageMin ?? undefined,
    age_max: ageMax ?? undefined,
    contact_type: contactType || undefined,
    status: status || undefined,
    possible_duplicates: reviewDups || undefined,
    with_documents: withDocs || undefined,
  };
  const { data, isLoading, isFetching, isError } = useSearchCrmClientsQuery({
    ...filters, letter: letter || undefined, sort, limit: PAGE_SIZE, offset: page * PAGE_SIZE,
  });
  const { data: summary } = useGetCrmSummaryQuery(filters);
  const { data: opts } = useGetCrmOptionsQuery();

  const clients = data?.clients || [];
  const total = data?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const sources = opts?.sources || [];
  const sTotal = summary?.total || 0;

  const lastBuilt = useMemo(() => {
    const stamps = sources.map((s) => s.last_pulled_at || s.last_built_at).filter(Boolean).sort();
    if (!stamps.length) return null;
    const d = new Date(String(stamps[0]).replace(' ', 'T') + 'Z');
    return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
  }, [sources]);

  const inputStyle = {
    border: `1px solid ${work.hairline}`, borderRadius: 6, padding: '0.4rem 0.7rem',
    fontSize: '0.86rem', outline: 'none', background: work.surface, color: work.ink, minWidth: 0,
  };
  const pad = isMobile ? '0.75rem' : '1rem 1.25rem';
  const statusLabel = STATUS_GROUPS.find(([k]) => k === statusGroup)?.[1] || 'Clients';

  return (
    <div style={{ background: work.canvas, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>
      <div style={{ background: work.surface, borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: work.deepBlue, borderBottom: `3px solid ${work.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <i className="bi bi-person-lines-fill" style={{ color: work.goldenrod }} />
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
            CRM
          </h5>
          {/* One pill per merging firm; "All firms" only matters once there are two. */}
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
            {sources.length > 1 && (
              <button type="button" onClick={() => set(setSource)('')} style={pillStyle(!source)}>All firms</button>
            )}
            {sources.map((s) => (
              <button
                key={s.label}
                type="button"
                onClick={() => set(setSource)(source === s.label ? '' : s.label)}
                style={pillStyle(sources.length === 1 || source === s.label)}
              >
                {s.display_name}
              </button>
            ))}
          </div>
          <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem', marginLeft: 'auto' }}>
            {isFetching ? 'searching...' : `${fmtInt(total)} ${statusLabel.toLowerCase()}`}
          </span>
          <button
            type="button"
            onClick={() => setAskOpen(!askOpen)}
            aria-expanded={askOpen}
            style={{ ...pillStyle(askOpen), display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
          >
            <i className="bi bi-chat-dots" />Ask
          </button>
        </div>

        {askOpen && (
          <AskPanel current={askCurrent} total={total} counting={isFetching} onApply={applyAsk} onReset={clearAll} onClose={() => setAskOpen(false)} />
        )}

        {/* Tiles */}
        <div style={{ padding: pad, borderBottom: `1px solid ${work.hairline}`, background: '#fafbfc' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0,1fr))' : 'repeat(4, minmax(0,1fr))', gap: '0.75rem' }}>
            <StatTile hero label={statusLabel} value={fmtInt(sTotal)} note={summary ? `${fmtInt(summary.no_advisor)} without an advisor` : null} />
            <StatTile hero label="Households" value={fmtInt(summary?.households)} note="as grouped in each firm's CRM" />
            <StatTile
              hero
              label="Duplicates"
              value={fmtInt(summary?.duplicates_grouped)}
              note={(
                <>
                  extra records folded in
                  {summary?.possible_duplicates > 0 && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        onClick={() => set(setReviewDups)(!reviewDups)}
                        aria-pressed={reviewDups}
                        title="Same name, nothing else in common - check these by hand"
                        style={{ background: 'none', border: 'none', padding: 0, color: work.tealBlue, fontWeight: reviewDups ? 700 : 400, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}
                      >
                        {fmtInt(summary.possible_duplicates)} to review
                      </button>
                    </>
                  )}
                </>
              )}
            />
            <StatTile
              hero
              label="Linked to Salesforce"
              value={`${pct(summary?.linked_fsc || 0, sTotal)}%`}
              note={(
                <>
                  {fmtInt(summary?.linked_fsc)} matched to an FSC household
                  {(summary?.with_documents > 0 || withDocs) && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        onClick={() => set(setWithDocs)(!withDocs)}
                        aria-pressed={withDocs}
                        title="Show only clients whose Salesforce household has documents"
                        style={{ background: 'none', border: 'none', padding: 0, color: work.tealBlue, fontWeight: withDocs ? 700 : 400, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}
                      >
                        {fmtInt(summary?.with_documents)} with documents
                      </button>
                    </>
                  )}
                </>
              )}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '1rem 0 0.5rem' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: work.muted }}>Products held</span>
            <span style={{ fontSize: '0.74rem', color: work.muted }}>
              {summary ? `${fmtInt(summary.no_products)} with none on record` : ''}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0,1fr))' : `repeat(${PRODUCTS.length}, minmax(0,1fr))`, gap: '0.5rem' }}>
            {PRODUCTS.map((p) => (
              <ProductTile
                key={p.key}
                product={p}
                count={summary?.products?.[p.key] || 0}
                total={sTotal}
                active={product === p.key}
                onClick={() => set(setProduct)(product === p.key ? '' : p.key)}
              />
            ))}
          </div>

          {(summary?.gemstones || []).length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.9rem' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: work.muted, marginRight: '0.25rem' }}>Gemstone</span>
              {summary.gemstones.map((g) => {
                const key = g.value || '__none__';
                const active = gemstone === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => set(setGemstone)(active ? '' : key)}
                    aria-pressed={active}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer',
                      border: `1px solid ${active ? work.tealBlue : work.hairline}`, borderRadius: 999,
                      background: active ? 'rgba(0,105,166,0.06)' : work.surface, padding: '0.15rem 0.6rem',
                    }}
                  >
                    {g.value ? <GemBadge value={g.value} gemstone={g.gemstone} /> : <span style={{ fontSize: '0.75rem', fontWeight: 700, color: work.muted }}>None</span>}
                    <span style={{ fontSize: '0.75rem', color: work.muted }}>{fmtInt(g.n)}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* JLS marks tax clients three ways and they disagree; each is its own
              filter until the firm decides which one counts. */}
          {((summary?.tax_methods || []).length > 0 || taxMethod) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
              <span
                title="Each firm-defined way of marking a tax client, counted separately. A client can match more than one."
                style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: work.muted, marginRight: '0.25rem' }}
              >
                Tax method
              </span>
              {(summary?.tax_methods || []).map((m) => {
                const active = taxMethod === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => set(setTaxMethod)(active ? '' : m.value)}
                    aria-pressed={active}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer',
                      border: `1px solid ${active ? work.tealBlue : work.hairline}`, borderRadius: 999,
                      background: active ? 'rgba(0,105,166,0.06)' : work.surface, padding: '0.15rem 0.6rem',
                    }}
                  >
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: work.ink }}>{m.label}</span>
                    <span style={{ fontSize: '0.75rem', color: work.muted }}>{fmtInt(m.n)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Filters */}
        <div style={{ padding: pad, display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center', borderBottom: `1px solid ${work.hairline}` }}>
          <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 0 }}>
            <i className="bi bi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: work.muted, fontSize: '0.8rem' }} />
            <input
              type="search"
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder="Search name, household, email, phone, city or ZIP"
              aria-label="Search clients"
              style={{ ...inputStyle, width: '100%', paddingLeft: 28 }}
            />
          </div>
          <div role="group" aria-label="Status" style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: `1px solid ${work.hairline}` }}>
            {STATUS_GROUPS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => set(setStatusGroup)(value)}
                aria-pressed={statusGroup === value}
                style={{
                  border: 'none', padding: '0.35rem 0.7rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                  background: statusGroup === value ? work.tealBlue : work.surface,
                  color: statusGroup === value ? 'white' : work.muted,
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <select value={advisor} onChange={(e) => set(setAdvisor)(e.target.value)} aria-label="Advisor" style={{ ...inputStyle, flex: '0 1 200px' }}>
            <option value="">All advisors</option>
            <option value="__none__">No advisor</option>
            {(opts?.advisors || []).map((a) => <option key={a.value} value={a.value}>{a.value}</option>)}
          </select>
          <select value={taxPreparer} onChange={(e) => set(setTaxPreparer)(e.target.value)} aria-label="Tax preparer" style={{ ...inputStyle, flex: '0 1 200px' }}>
            <option value="">All tax preparers</option>
            {(opts?.tax_preparers || []).map((a) => <option key={a.value} value={a.value}>{a.value}</option>)}
          </select>
          {hasFilters && (
            <button type="button" onClick={clearAll} className="btn btn-sm btn-outline-secondary">Clear</button>
          )}
        </div>

        {/* Filters only Ask can set, each removable here. */}
        {askOnly.length > 0 && (
          <div style={{ padding: isMobile ? '0.5rem 0.75rem' : '0.5rem 1.25rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', borderBottom: `1px solid ${work.hairline}` }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: work.muted }}>Also filtered</span>
            {askOnly.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => { c.clear(); setPage(0); }}
                title="Remove this filter"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', border: `1px solid ${work.tealBlue}`, borderRadius: 999, background: 'rgba(0,105,166,0.06)', padding: '0.15rem 0.6rem', fontSize: '0.75rem', fontWeight: 700, color: work.ink }}
              >
                {c.label}<i className="bi bi-x" />
              </button>
            ))}
          </div>
        )}

        {/* A-Z */}
        <div style={{ padding: isMobile ? '0.5rem 0.75rem' : '0.5rem 1.25rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap', borderBottom: `1px solid ${work.hairline}` }}>
          <button type="button" onClick={() => set(setLetter)('')} style={letterStyle(letter === '')}>All</button>
          {LETTERS.map((L) => (
            <button key={L} type="button" onClick={() => set(setLetter)(letter === L ? '' : L)} style={letterStyle(letter === L)}>{L}</button>
          ))}
        </div>

        {/* Table */}
        <div style={{ padding: isMobile ? '0.5rem' : '0.5rem 1.25rem' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '2.5rem' }}>
              <div className="spinner-border" style={{ color: work.tealBlue }} />
            </div>
          ) : isError ? (
            <p style={{ color: '#b42318', textAlign: 'center', padding: '2rem 0', margin: 0 }}>Could not load clients.</p>
          ) : clients.length === 0 ? (
            <p style={{ color: work.muted, textAlign: 'center', padding: '2rem 0', margin: 0 }}>
              {hasFilters ? 'No clients match those filters.' : 'No CRM data has been built yet.'}
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle" style={{ fontSize: '0.86rem', marginBottom: 0 }}>
                <thead>
                  <tr style={{ color: work.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <SortHeader label="Client" field="name" sort={sort} setSort={set(setSort)} />
                    <th>Status</th>
                    <SortHeader label="Advisor" field="advisor" sort={sort} setSort={set(setSort)} />
                    <th>Gemstone</th>
                    <th>Products</th>
                    <th>Contact</th>
                    <th>Location</th>
                    <SortHeader label="Client since" field="client_since" sort={sort} setSort={set(setSort)} />
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.id}>
                      <td style={{ minWidth: 180 }}>
                        <Link to={`/work/crm/${c.id}`} style={{ color: work.deepBlue, fontWeight: 700, textDecoration: 'none' }}>
                          {c.last_name && c.first_name ? `${c.last_name}, ${c.first_name}` : c.display_name}
                        </Link>
                        {c.dup_count > 1 && (
                          <span title={`${c.dup_count} records in the source CRM for this person`} style={{ marginLeft: 6, fontSize: '0.66rem', fontWeight: 700, color: work.goldenrod, border: `1px solid ${work.goldenrod}`, borderRadius: 4, padding: '0 0.3rem' }}>
                            {c.dup_count} records
                          </span>
                        )}
                        {c.possible_dups > 0 && (
                          <i className="bi bi-exclamation-circle" title={`${c.possible_dups} other record(s) with the same name - possibly the same person`} style={{ marginLeft: 5, color: work.goldenrod }} />
                        )}
                        {c.fsc_household_id && (
                          <i className="bi bi-link-45deg" title="Linked to a Salesforce FSC household" style={{ marginLeft: 4, color: work.tealBlue }} />
                        )}
                        {c.doc_count > 0 && (
                          <Link
                            to={`/work/crm/${c.id}#documents`}
                            title={`${c.doc_count} document${c.doc_count === 1 ? '' : 's'}`}
                            style={{ marginLeft: 5, color: work.goldenrod, fontSize: '0.75rem', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}
                          >
                            <i className="bi bi-folder2-open" /> {c.doc_count}
                          </Link>
                        )}
                        <div style={{ fontSize: '0.74rem', color: work.muted }}>
                          {[c.household_name, sources.length > 1 ? c.source_name : null].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td style={{ color: work.muted, whiteSpace: 'nowrap' }}>{c.status || '-'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {c.advisor_name || <span style={{ color: work.muted }}>-</span>}
                        {c.tax_preparer && <div style={{ fontSize: '0.72rem', color: work.muted }}>Tax: {c.tax_preparer}</div>}
                      </td>
                      <td><GemBadge value={c.gemstone_source} gemstone={c.gemstone} /></td>
                      <td style={{ maxWidth: 220 }}><ProductPills row={c} /></td>
                      <td style={{ fontSize: '0.8rem' }}>
                        {c.email && <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{c.email}</div>}
                        {c.phone && <div style={{ color: work.muted }}>{c.phone}</div>}
                        {!c.email && !c.phone && <span style={{ color: work.muted }}>-</span>}
                      </td>
                      <td style={{ color: work.muted, whiteSpace: 'nowrap' }}>{[c.city, c.state].filter(Boolean).join(', ') || '-'}</td>
                      <td style={{ color: work.muted, whiteSpace: 'nowrap' }}>{c.client_since ? new Date(c.client_since + 'T00:00:00').getFullYear() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {total > PAGE_SIZE && (
          <div style={{ padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', borderTop: `1px solid ${work.hairline}` }}>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span style={{ color: work.muted, fontSize: '0.82rem' }}>Page {page + 1} of {fmtInt(pageCount)}</span>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        )}
      </div>

      <p style={{ color: work.muted, fontSize: '0.75rem', marginTop: '0.75rem', marginBottom: 0 }}>
        {sources.length
          ? `From ${sources.map((s) => `${s.display_name} (${s.source_system === 'smartoffice' ? 'SmartOffice' : 'Redtail'})`).join(', ')}`
          : 'No firm CRM loaded yet'}
        {lastBuilt ? ` · pulled ${lastBuilt}` : ''}
        {summary?.open_balance ? ` · ${fmtMoney(summary.open_balance)} in open account balances on record` : ''}
      </p>
    </div>
  );
}

/** Filters from the page URL, in the API's own parameter names. Unknown
 * values fall back to the defaults; the API validates whatever remains. */
function readUrlFilters(search) {
  const p = new URLSearchParams(search);
  const str = (k) => (p.get(k) || '').trim();
  const list = (k) => str(k).split(',').filter((x) => PRODUCTS.some((pr) => pr.key === x));
  const num = (k) => { const v = parseInt(str(k), 10); return Number.isInteger(v) && v >= 0 && v <= 120 ? v : null; };
  const group = str('status_group');
  const state = str('state').toUpperCase();
  return {
    q: str('q'), source: str('source'), advisor: str('advisor'), gemstone: str('gemstone'),
    status_group: STATUS_GROUPS.some(([k]) => k === group) ? group : 'clients',
    status: str('status'), tax_method: str('tax_method'), contact_type: str('contact_type'),
    products: list('products'), exclude_products: list('exclude_products'),
    city: str('city'), state: /^[A-Z]{2}$/.test(state) ? state : '',
    age_min: num('age_min'), age_max: num('age_max'),
    possible_duplicates: str('possible_duplicates') === 'true', with_documents: str('with_documents') === 'true',
  };
}

const productName = (key) => PRODUCTS.find((p) => p.key === key)?.label || key;

const ASK_EXAMPLES = [
  'Show me all clients that are tax but not Medicare',
  'Clients in Red Bank, NJ',
  'One Team prospects over 70 with no advisor',
];

/**
 * "Ask": type a question, get the list filtered. The server turns the question
 * into the page's own filters (it never reads client records); the answer
 * replaces the filters, so every control and chip shows what was applied.
 */
function AskPanel({ current, total, counting, onApply, onReset, onClose }) {
  const [ask, { isLoading }] = useAskCrmMutation();
  const [text, setText] = useState('');
  const [messages, setMessages] = useState([]);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [messages, isLoading]);

  const send = async (question) => {
    const qn = (question ?? text).trim();
    if (!qn || isLoading) return;
    setText('');
    setMessages((m) => [...m, { role: 'user', text: qn }]);
    try {
      const res = await ask({ question: qn, current }).unwrap();
      if (!res.needs_clarification) onApply(res.filters);
      setMessages((m) => [...m, { role: 'assistant', text: res.reply, note: res.unsupported, counts: !res.needs_clarification }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', error: true, text: e?.data?.detail || 'Something went wrong - try again, or use the filters.' }]);
    }
  };
  const restart = () => { setMessages([]); setText(''); onReset(); };
  const last = messages[messages.length - 1];

  return (
    <div style={{ borderBottom: `1px solid ${work.hairline}`, background: '#f7f9fc', padding: '0.75rem 1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <i className="bi bi-chat-dots" style={{ color: work.goldenrod }} />
        <strong style={{ fontSize: '0.8rem', color: work.ink, textTransform: 'uppercase', letterSpacing: '1px' }}>Ask</strong>
        <span style={{ fontSize: '0.74rem', color: work.muted }}>Describe who you want to see - Ask sets the filters below. It never reads client records.</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
          {messages.length > 0 && (
            <button type="button" onClick={restart} className="btn btn-sm btn-outline-secondary">New search</button>
          )}
          <button type="button" onClick={onClose} className="btn btn-sm btn-link" aria-label="Close Ask" style={{ color: work.muted }}>
            <i className="bi bi-x-lg" />
          </button>
        </div>
      </div>

      {messages.length === 0 ? (
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
          {ASK_EXAMPLES.map((ex) => (
            <button key={ex} type="button" onClick={() => send(ex)} style={{ border: `1px solid ${work.hairline}`, background: work.surface, borderRadius: 999, padding: '0.2rem 0.7rem', fontSize: '0.78rem', color: work.tealBlue, cursor: 'pointer' }}>
              {ex}
            </button>
          ))}
        </div>
      ) : (
        <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.6rem' }}>
          {messages.map((m, i) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '80%',
                background: m.role === 'user' ? work.tealBlue : work.surface,
                color: m.role === 'user' ? 'white' : (m.error ? '#b42318' : work.ink),
                border: m.role === 'user' ? 'none' : `1px solid ${work.hairline}`,
                borderRadius: 10, padding: '0.4rem 0.7rem', fontSize: '0.84rem',
              }}
            >
              {m.text}
              {m.note && <div style={{ fontSize: '0.74rem', color: work.muted, marginTop: 2 }}>Not filtered: {m.note}</div>}
              {m.counts && m === last && (
                <div style={{ fontSize: '0.74rem', color: work.muted, marginTop: 2 }}>{counting ? 'counting...' : `${fmtInt(total)} found`}</div>
              )}
            </div>
          ))}
          {isLoading && <div style={{ alignSelf: 'flex-start', fontSize: '0.8rem', color: work.muted }}>thinking...</div>}
          <div ref={endRef} />
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); send(); }} style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={500}
          placeholder={messages.length ? 'Refine it - "now only prospects", "of those, who is in NJ"' : 'e.g. clients with an annuity and no advisor'}
          aria-label="Ask about the client list"
          style={{ flex: 1, minWidth: 0, border: `1px solid ${work.hairline}`, borderRadius: 6, padding: '0.45rem 0.7rem', fontSize: '0.86rem', outline: 'none' }}
        />
        <button type="submit" className="btn btn-sm" disabled={isLoading || !text.trim()} style={{ background: work.tealBlue, color: 'white' }}>
          {isLoading ? <span className="spinner-border spinner-border-sm" /> : 'Ask'}
        </button>
      </form>
    </div>
  );
}
AskPanel.propTypes = {
  current: PropTypes.object.isRequired, total: PropTypes.number, counting: PropTypes.bool,
  onApply: PropTypes.func.isRequired, onReset: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired,
};

function pillStyle(active) {
  return {
    border: `1px solid ${active ? work.goldenrod : 'rgba(255,255,255,0.3)'}`,
    background: active ? 'rgba(208,155,44,0.18)' : 'transparent',
    color: 'white', borderRadius: 999, padding: '0.1rem 0.65rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
  };
}

function letterStyle(active) {
  return {
    minWidth: 26, padding: '0.12rem 0.35rem', fontSize: '0.76rem', fontWeight: 700, borderRadius: 4, cursor: 'pointer',
    border: `1px solid ${active ? work.deepBlue : work.hairline}`,
    background: active ? work.deepBlue : work.surface,
    color: active ? 'white' : work.muted,
  };
}
